import { nextTick, type MaybeRefOrGetter, type Ref, shallowRef, toValue, watch } from "vue";
import { useDebounceFn, useResizeObserver, useThrottleFn, useWindowSize } from "@vueuse/core";
import type { DSPopoverPlacement } from "../types/props";
import { applyViewportConstraints, calculatePopoverCoordinates } from "../utils/positioning";

interface UsePopoverStylesOptions {
  activatorRef: Ref<HTMLElement | undefined>;
  contentRef: Ref<HTMLElement | undefined>;
  placement: MaybeRefOrGetter<DSPopoverPlacement>;
  offset: MaybeRefOrGetter<number>;
  inheritActivatorWidth: boolean;
}

type PopoverStyles = Record<string, string>;
const VIEWPORT_PADDING = 8;

export const usePopoverStyles = ({
  activatorRef,
  contentRef,
  placement,
  offset,
  inheritActivatorWidth,
}: UsePopoverStylesOptions) => {
  const contentStyles = shallowRef<PopoverStyles>({
    visibility: "hidden",
    position: "fixed",
  });

  const areElementsValid = (): boolean => {
    return !!(activatorRef.value && contentRef.value);
  };

  const getValidRectangles = () => {
    if (!areElementsValid()) return null;

    const activatorRect = activatorRef.value?.getBoundingClientRect();
    const contentRect = contentRef.value!.getBoundingClientRect();

    if (contentRect.width === 0 || contentRect.height === 0) {
      return null;
    }

    return { activatorRect, contentRect };
  };

  const getAvailableWidth = (): number => Math.max(0, window.innerWidth - VIEWPORT_PADDING * 2);

  const getEffectiveContentRect = (
    activatorRect: DOMRect,
    contentRect: DOMRect,
    inheritActivatorWidth: boolean
  ): DOMRect => {
    if (!inheritActivatorWidth) return contentRect;

    const availableWidth = getAvailableWidth();
    const minWidth = Math.min(activatorRect.width, availableWidth);
    const width = Math.min(Math.max(contentRect.width, minWidth), availableWidth);

    return new DOMRect(contentRect.x, contentRect.y, width, contentRect.height);
  };

  const computeConstrainedPositions = (activatorRect: DOMRect, contentRect: DOMRect) => {
    const { top, left } = calculatePopoverCoordinates({
      activatorRect,
      contentRect,
      placement: toValue(placement),
      offset: toValue(offset),
    });

    return applyViewportConstraints({
      position: { top, left },
      contentRect,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      padding: VIEWPORT_PADDING,
    });
  };

  const createStyles = (
    position: { top: number; left: number },
    inheritActivatorWidth: boolean,
    activatorRect: DOMRect
  ): PopoverStyles => {
    const styles: PopoverStyles = {
      top: `${position.top}px`,
      left: `${position.left}px`,
      visibility: contentStyles.value.visibility,
      position: "fixed",
    };

    if (inheritActivatorWidth) {
      const availableWidth = getAvailableWidth();

      styles.minWidth = `${Math.min(activatorRect.width, availableWidth)}px`;
      styles.maxWidth = `${availableWidth}px`;
    }

    return styles;
  };

  const shouldUpdateStyles = (newStyles: PopoverStyles): boolean => {
    return JSON.stringify(newStyles) !== JSON.stringify(contentStyles.value);
  };

  const calculateStyles = async () => {
    await nextTick();

    const rectangles = getValidRectangles();
    if (!rectangles?.activatorRect) return;

    const effectiveContentRect = getEffectiveContentRect(
      rectangles.activatorRect,
      rectangles.contentRect,
      inheritActivatorWidth
    );
    const constrainedPosition = computeConstrainedPositions(rectangles.activatorRect, effectiveContentRect);

    const newStyles = createStyles(constrainedPosition, inheritActivatorWidth, rectangles.activatorRect);

    if (shouldUpdateStyles(newStyles)) {
      contentStyles.value = newStyles;
    }
  };

  const setVisibility = (visibility: "visible" | "hidden") => {
    contentStyles.value = {
      ...contentStyles.value,
      visibility,
    };
  };

  const isVisible = () => contentStyles.value.visibility === "visible";

  const throttledCalculateStyles = useThrottleFn(calculateStyles, 16);
  const debouncedCalculateStyles = useDebounceFn(calculateStyles, 100);

  const handleRestylingIfVisible = async () => {
    if (isVisible()) {
      await debouncedCalculateStyles();
    }
  };

  watch([() => toValue(placement), () => toValue(offset)], () => {
    if (isVisible()) void calculateStyles();
  });

  const { width: windowWidth, height: windowHeight } = useWindowSize();
  watch([windowWidth, windowHeight], handleRestylingIfVisible);

  useResizeObserver(contentRef, handleRestylingIfVisible);
  useResizeObserver(activatorRef, handleRestylingIfVisible);

  return {
    contentStyles,
    calculateStyles: throttledCalculateStyles,
    setVisibility,
    isVisible,
  };
};
